import type { NotificationType, PFSNotifications, PFSNotificationResponseDTO } from '~/app/types/notificationType';
import type { PFSNotification } from '~/app/types/notificationType';
import { RouteNames } from '~/app/routes/routeNames';

// TODO handle internal user
export async function useActiveNotifications(
  floristId: string | undefined,
  isInternalUser: boolean
): Promise<PFSNotifications> {
  const emptyResult = Promise.resolve({
    orders: [],
    cancellations: [],
    modifications: [],
    expired: [],
  });
  if (!isInternalUser) {
    const url = `${useMsUrls().notificationSupplierBaseUrl}/ActiveNotifications/${floristId}`;
    try {
      return await useCustomFetch<PFSNotifications>(url);
    } catch {
      return emptyResult;
    }
  } else {
    return emptyResult;
  }
}

export async function useLastNotifications(floristId: string | undefined): Promise<PFSNotification[]> {
  const url = `${useMsUrls().notificationSupplierBaseUrl}/LastsNotifications/${floristId}`;
  try {
    return await useCustomFetch<PFSNotification[]>(url);
  } catch {
    return Promise.resolve([]);
  }
}

/* NOSTATUS notifications are created by 2 CRONs running at the end of the morning and end of the afternoon
HERE https://dev.azure.com/OCTO-MS/IT.Microservices.Cron.GenerateMorningNoStatusOrderNotifications
HERE https://dev.azure.com/OCTO-MS/IT.Microservices.Cron.GenerateAfternoonNoStatusOrderNotification
To change the schedule, edit the chart-values, key "schedule"
=> Time is in UTC, subtract 2h from FR time
Eg. schedule: "30 16 * * *" means 16:30 UTC = 18:30 FR
*/

export async function getNotificationsByStatus({
  floristId,
  status,
  from,
}: {
  floristId: string;
  status: NotificationType;
  from?: string; // YYYY-MM-DD
}): Promise<PFSNotification[]> {
  const url = `${useMsUrls().notificationSupplierBaseUrl}/NotificationsByType/${floristId}/${status}${from ? `?from=${from}` : ''}`;
  try {
    const dtos = await useCustomFetch<PFSNotificationResponseDTO[]>(url);
    return dtos.map((dto) => ({
      id: dto.id ?? '',
      isRead: dto.isRead,
      orderId: dto.orderId ?? '',
      orderNumber: dto.orderNumber ?? '',
      type: dto.type as PFSNotification['type'],
    }));
  } catch {
    return [];
  }
}

export async function markNotificationAsRead(
  floristId: string | undefined,
  notificationIds: string[]
): Promise<boolean> {
  const url = `${useMsUrls().notificationSupplierBaseUrl}/MarkNotificationAsRead/${floristId}`;
  try {
    const response = await useRawCustomFetch(url, {
      method: 'POST',
      body: notificationIds,
    });
    return response.status === 200;
  } catch (e) {
    console.error(e);
    return false;
  }
}

export async function markNotificationAsReadByFloristIdAndOrderId(request: {
  floristId: string;
  ctId: string;
}): Promise<boolean> {
  const url = `${useMsUrls().notificationSupplierBaseUrl}/MarkNotificationAsRead/${request.floristId}&${request.ctId}`;
  try {
    return await useCustomFetch(url, {
      method: 'POST',
    });
  } catch (e) {
    console.error(e);
    return Promise.resolve(false);
  }
}

export function toLink(router: any, length: number, id: string, type: any) {
  if (type !== 'cancellation') {
    if (type === 'refused') {
      return '';
    } else {
      if (length > 1) {
        return router.resolve({
          name: RouteNames.OrderList,
        }).fullPath;
      } else {
        if (id) {
          return router.resolve({
            name: RouteNames.OrderView,
            params: { id: id },
          }).fullPath;
        }
        return router.resolve({
          name: RouteNames.OrderList,
        }).fullPath;
      }
    }
  } else {
    return '';
  }
}

export function getNotificationsIds(notifications: PFSNotification[]): string[] {
  const notificationsIds: string[] = [];
  notifications.forEach((n) => {
    notificationsIds.push(n.id);
  });
  return notificationsIds;
}

export async function markAllNotificationsOfTypeAsRead(
  floristId: string | undefined,
  notifications: PFSNotification[]
): Promise<boolean> {
  if (!notifications || notifications.length === 0) {
    return Promise.resolve(false);
  }

  const notificationIds = getNotificationsIds(notifications);
  return await markNotificationAsRead(floristId, notificationIds);
}
